import { ENDPOINT } from '~/constants';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { toSnakeCase } from '@vestigas/utils';
import { i18n, log } from '~/utils';

import { ACCEPT_STATE_OPTIONS, PROCESS_STATE_OPTIONS } from './constants';
import {
  type DeliveryNoteFilterSuggestionsQueryParameters,
  type DeliveryNoteFilterSuggestionsResponse,
} from './types';
import { zodDeliveryNoteFilterSuggestionsParamsSchema } from './validations';

/**
 * Fetches possible values for a delivery notes filter from the API.
 * Handles pagination and error cases while enforcing API limits.
 *
 * @param queryParams - Parameters for the filter suggestions query
 * @param queryParams.entity - The entity type to get suggestions for
 * @param queryParams.filterConfig - Optional filter configuration
 * @param queryParams.limit - Maximum number of suggestions to return (max 200)
 * @param queryParams.offset - Pagination offset
 * @param queryParams.searchValue - Optional search term to filter suggestions
 * @returns Promise containing array of suggestion strings
 * @throws Error if API request fails
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Analytics/operation/compute_dln_analytics_suggestions_analytics_suggestions_post
 */
export const fetchDeliveryNoteFilterSuggestions = async (
  queryParams: DeliveryNoteFilterSuggestionsQueryParameters,
  options?: KyRequestOptions,
) => {
  try {
    const entity = toSnakeCase(queryParams.entity);

    switch (entity) {
      case 'process_state': {
        // Don't query the API - this is only a short list of predefined options.
        return PROCESS_STATE_OPTIONS;
      }

      case 'accept_state':
      case 'dln_accept_state': {
        // Don't query the API - this is only a short list of predefined options.
        return ACCEPT_STATE_OPTIONS;
      }

      case 'settled_status': {
        // Don't query the API - this is only a short list of predefined options.
        return [
          i18n.t(($) => $.yes, { ns: 'common' }),
          i18n.t(($) => $.no, { ns: 'common' }),
        ];
      }

      default: {
        const response = await vestigasApi
          .post(ENDPOINT.DELIVERY.GET_FILTER_SUGGESTIONS(), {
            json: queryParams,
            validate: { body: zodDeliveryNoteFilterSuggestionsParamsSchema },
            ...options,
          })
          .json<DeliveryNoteFilterSuggestionsResponse>();

        return response.items;
      }
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Delivery Note FilterSuggestions',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getFilterSuggestions.error422,
            {
              ns: 'data',
            },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getFilterSuggestions.error.generic,
            { ns: 'data' },
          );
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
